import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from "@angular/core";
import { MenuItem, MenuItemType } from "@app/core/models/sidenav.model";
import { ComponentCanDeactivate, PendingChangesGuard } from "@app/project/services/pending-changes.guard";
import { Observable, Subject } from "rxjs";

import { TemplatePortal } from "@angular/cdk/portal";
import { FormBuilder } from "@angular/forms";
import { AuthUserService } from "@app/auth/services/auth-user.service";
import { RouterService } from "@app/root-store/router.service";
import { NgChanges } from "@app/shared/model/ng-changes.model";
import { MenuUtility } from "@app/shared/services/utils/menu-utility.service";
import { User } from "firebase/auth";

@Component({
    selector: "clare-sidenav-new",
    templateUrl: "./sidenav-new.component.html",
    styleUrls: ["./sidenav-new.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavNewComponent implements OnInit, OnChanges, OnDestroy, ComponentCanDeactivate {
    @Input() user: User;
    @Input() isMobile = false;

    @ViewChild("accountSearch", { static: true }) private accountSearch: TemplateRef<any>;
    @ViewChild("authPanel", { static: true }) private authPanel: TemplateRef<any>;
    @ViewChild("searchInput", { static: false }) private searchInput: ElementRef<any>;
    blankPageConfig = "noopener,noreferrer";
    upperMenuItems = [
        new MenuItem({
            id: "home",
            name: "home",
            textLabel: "Home",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-home",
            navigation: "/dashboard",
            permissionsExcept: ["viewCustomerPortal"],
        }),
        new MenuItem({
            id: "account_list",
            name: "accounts",
            textLabel: "Accounts",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-accounts",
            navigation: "/accounts",
            permissionsOnly: ["viewProject", "editProject"],
        }),
        new MenuItem({
            id: "account_create",
            name: "create account",
            textLabel: "Create Account",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-plus_filled",
            navigation: "/add-account",
            permissionsOnly: ["editProject"],
        }),
        new MenuItem({
            id: "account_search",
            name: "search account",
            textLabel: "Search Account",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-search",
            styleClasses: ["no-url-item"],
            nestedMenu: (nestedMenuWrapper: HTMLElement) => {
                const wrapper = this.setupOverlayMenuItem(nestedMenuWrapper);
                this.openAccountSearch(wrapper, "account-search");
            },
            navigation: null,
        }),
        new MenuItem({
            id: "customers",
            name: "customers",
            textLabel: "Customers",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-customer",
            navigation: "/customers",
            permissionsOnly: ["viewCustomerList"],
        }),
        new MenuItem({
            id: "companies",
            name: "companies",
            textLabel: "Companies",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-company",
            navigation: "/entities",
            permissionsOnly: ["viewCompany", "editCompany", "editCompanySettings", "updateLockoutScreen"],
        }),
        new MenuItem({
            id: "all_staff",
            name: "all staff",
            textLabel: "All Staff",
            itemType: MenuItemType.MAIN_UPPER,
            icon: "fp-icon-staff",
            navigation: "/all-staff",
            permissionsOnly: ["editAllCustomers"],
        }),
    ];

    lowerMenuItems = [
        new MenuItem({
            id: "guide",
            name: "guide",
            textLabel: "Guide",
            itemType: MenuItemType.MAIN_LOWER,
            icon: "fp-icon-info_bordered",
            navigation: () => {
                window.open("https://www.clarecontrols.com/helpcenter/fusionpro-user-guide-doc-id-1918", "_blank", this.blankPageConfig);
            },
            tooltip: { isAuthPanel: false, text: `Infostream` },
            permissionsExcept: ["viewCustomerPortal", "viewPMSideNav"],
        }),
        new MenuItem({
            id: "guide",
            name: "guide",
            textLabel: "Guide",
            itemType: MenuItemType.MAIN_LOWER,
            icon: "fp-icon-info_bordered",
            navigation: () => {
                window.open("https://www.clarecontrols.com/helpcenter/mdu-property-management", "_blank", this.blankPageConfig);
            },
            permissionsOnly: ["viewPMSideNav"],
        }),
        new MenuItem({
            id: "whats_new",
            name: "what's new",
            textLabel: "What's New",
            itemType: MenuItemType.MAIN_LOWER,
            icon: "fp-icon-announcement",
            navigation: () => {
                window.open("https://www.clarecontrols.com/en/helpcenter/whats-new", "_blank", this.blankPageConfig);
            },
            tooltip: { isAuthPanel: false, text: `What's New` },
            permissionsOnly: ["viewWhatsNew"],
        }),
        new MenuItem({
            id: "info",
            name: "info",
            textLabel: "Info",
            description: "Info",
            itemType: MenuItemType.MAIN_LOWER,
            icon: "fp-icon-customer",
            navigation: null,
            nestedMenu: (nestedMenuWrapper: HTMLElement) => {
                this.setupOverlayMenuItem(nestedMenuWrapper, () => this.openAuthPanel(nestedMenuWrapper, "auth-panel"));
            },
            tooltip: { isAuthPanel: true, text: `` },
        }),
    ];

    warningDialogConfig = {
        title: "Leave Page",
    };

    queryString = "";

    // for later filter implementation
    filterForm = this.fb.group({
        firstOrLast: [true],
        address: [true],
        companyName: [true],
        city: [true],
        phone: [true],
        email: [true],
        accountNumber: [true],
        uuid: [true],
    });

    private _currentNestedWrapper: HTMLElement = null;
    private _accountSearchClosed$ = new Subject<boolean>();
    private _accountSearchOverlay: OverlayRef = null;

    private _authPanelClosed$ = new Subject<boolean>();
    private _authPanelOverlay: OverlayRef = null;

    constructor(
        private readonly routerService: RouterService,
        private readonly overlay: Overlay,
        private readonly viewContainerRef: ViewContainerRef,
        private readonly authUserService: AuthUserService,
        private readonly fb: FormBuilder,
        private readonly pendingChangesGuard: PendingChangesGuard,
        private readonly menuService: MenuUtility
    ) {}

    ngOnChanges(changes: NgChanges<SidenavNewComponent>): void {
        if (changes.isMobile) {
            this.closeAllOverlayPanel();
        }
    }

    ngOnDestroy(): void {
        this.closeAllOverlayPanel();
    }
    canDeactivate(): Observable<boolean> | boolean {
        this.warningDialogConfig.title = `Leave Page`;
        // returning true will navigate without confirmation
        // returning false will show a confirm dialog before navigating away
        return false;
    }

    ngOnInit(): void {
        this._accountSearchClosed$.subscribe(_ => {
            if (this._accountSearchOverlay) {
                this._accountSearchOverlay.dispose();
            }
            this.deacticateNestedMenuWrapper();
        });

        this._authPanelClosed$.subscribe(_ => {
            if (this._authPanelOverlay) {
                this._authPanelOverlay.dispose();
            }
            this.deacticateNestedMenuWrapper();
        });
    }

    private openAuthPanel(connectedTo: HTMLElement, panelClass = "") {
        this._authPanelOverlay = this.getOverlaySetup(connectedTo, panelClass);
        this._authPanelOverlay.attach(new TemplatePortal(this.authPanel, this.viewContainerRef));
        this._authPanelOverlay.backdropClick().subscribe(() => {
            this._authPanelClosed$.next(true);
        });
    }

    onNavigate(navigationEvent: string | (() => any)) {
        if (!navigationEvent) {
            return;
        }

        if (typeof navigationEvent === "string") {
            this.routerService.navigateToUrl([navigationEvent]);
            return;
        }

        if (typeof navigationEvent === "function") {
            navigationEvent();
            return;
        }

        console.warn("wrong router setting up");
    }

    onSearchingEvents(event?: KeyboardEvent) {
        if (!event || event.key === "Enter") {
            if (this.queryString) {
                this.routerService.navigateToUrl(["/accounts"], null, {
                    query: this.queryString,
                });
            } else {
                this.routerService.navigateToUrl(["/accounts"]);
            }
            this.menuService.requestCloseSubMenu();
            if (this._accountSearchOverlay) {
                this._accountSearchOverlay.dispose();
            }
            this.deacticateNestedMenuWrapper();
        }
    }

    onDataRefresh() {
        // TODO
    }

    logout() {
        this.processLogout();
    }

    private setupOverlayMenuItem(nestedMenuWrapper: HTMLElement, eventHandler?: () => any) {
        this._currentNestedWrapper = nestedMenuWrapper;
        const wrapper = nestedMenuWrapper.closest(".menu-item-wrapper");
        const isActive = wrapper.classList?.contains("active");
        if (!isActive) {
            wrapper.classList.add("active");

            if (eventHandler) {
                eventHandler();
            }
        }

        return wrapper as HTMLElement;
    }

    private deacticateNestedMenuWrapper() {
        if (!this._currentNestedWrapper) {
            return;
        }
        const wrapper = this._currentNestedWrapper.closest(".menu-item-wrapper");
        const isActive = wrapper.classList?.contains("active");
        if (isActive) {
            wrapper.classList.remove("active");
        }
    }

    private openAccountSearch(connectedTo: HTMLElement, panelClass = "") {
        this._accountSearchOverlay = this.getOverlaySetup(connectedTo, panelClass);
        this._accountSearchOverlay.attach(new TemplatePortal(this.accountSearch, this.viewContainerRef));
        this._accountSearchOverlay.backdropClick().subscribe(() => {
            this._accountSearchClosed$.next(true);
        });

        // autofocus should be delayed for awhile due to element creation
        setTimeout(() => {
            if (this.searchInput?.nativeElement) {
                this.searchInput.nativeElement.focus();
            }
        }, 200);
    }

    private getOverlaySetup(connectedTo: HTMLElement, panelClass = "", isFixedHeight = false) {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(connectedTo)
            .withPositions([
                {
                    originX: "end",
                    originY: "top",
                    overlayX: "start",
                    overlayY: "top",
                },
            ]);
        const configs = new OverlayConfig({
            hasBackdrop: true,
            panelClass: ["modal", panelClass, "is-active", "justify-center"],
            backdropClass: ["cdk-overlay-backdrop-showing"],
            positionStrategy,
        });
        if (isFixedHeight) {
            configs.height = connectedTo.offsetHeight ?? 55;
        }
        return this.overlay.create(configs);
    }

    private processLogout() {
        this.authUserService.signOutConfirmation();
    }

    private closeAllOverlayPanel() {
        this._accountSearchClosed$.next(true);
        this._authPanelClosed$.next(true);
    }
}
